import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * Paginated results list. On collection pages, fetches section with sort=price-ascending
 * to get canonical representative IDs (lowest price per group), then hides non-rep cards.
 */
export default class ResultsList extends PaginatedList {
  /** @type {Set<string> | null} */
  #canonicalRepresentativeIds = null;

  /** @type {MutationObserver | undefined} */
  #gridObserver;

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');

    requestIdleCallback(() => this.#applyRepresentativeFilter(), { timeout: 100 });
  }

  disconnectedCallback() {
    this.#gridObserver?.disconnect();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    super.disconnectedCallback();
  }

  async #applyRepresentativeFilter() {
    const collectionUrl = this.dataset.collectionUrl;
    if (!collectionUrl) return;

    const currentUrl = new URL(window.location.href);
    const sortBy = currentUrl.searchParams.get('sort_by') || '';
    const usePriceSortedReps = sortBy !== 'price-ascending';

    if (usePriceSortedReps) {
      try {
        const priceSortedUrl = new URL(collectionUrl, window.location.origin);
        priceSortedUrl.searchParams.set('sort_by', 'price-ascending');
        const responseText = await sectionRenderer.getSectionHTML(this.sectionId, false, priceSortedUrl);
        const sectionHtml = this.#extractSectionHtml(responseText);
        const parser = new DOMParser();
        const doc = parser.parseFromString(sectionHtml, 'text/html');
        const el = doc.querySelector('[data-representative-ids]');
        const idsStr = el?.getAttribute('data-representative-ids') || '';
        const ids = idsStr.split('|').map((s) => s.trim()).filter(Boolean);
        this.#canonicalRepresentativeIds = new Set(ids);
        if (this.#canonicalRepresentativeIds.size === 0) {
          const fallback = (this.dataset.representativeIds || '').split('|').map((s) => s.trim()).filter(Boolean);
          this.#canonicalRepresentativeIds = new Set(fallback);
        }
      } catch {
        const idsStr = this.dataset.representativeIds || '';
        this.#canonicalRepresentativeIds = new Set(
          idsStr.split('|').map((s) => s.trim()).filter(Boolean)
        );
      }
    } else {
      const idsStr = this.dataset.representativeIds || '';
      this.#canonicalRepresentativeIds = new Set(
        idsStr.split('|').map((s) => s.trim()).filter(Boolean)
      );
    }

    this.#hideNonRepresentativeCards();
    this.#updateCanonicalCount();

    const grid = this.#getGrid();
    if (grid && !this.#gridObserver) {
      this.#gridObserver = new MutationObserver(() => this.#hideNonRepresentativeCards());
      this.#gridObserver.observe(grid, { childList: true, subtree: true });
    } else if (!grid) {
      setTimeout(() => {
        this.#hideNonRepresentativeCards();
        this.#updateCanonicalCount();
        const g = this.#getGrid();
        if (g && !this.#gridObserver) {
          this.#gridObserver = new MutationObserver(() => this.#hideNonRepresentativeCards());
          this.#gridObserver.observe(g, { childList: true, subtree: true });
        }
      }, 400);
    }
    setTimeout(() => {
      this.#hideNonRepresentativeCards();
      this.#updateCanonicalCount();
    }, 600);
  }

  /**
   * Section Rendering with section_id returns HTML (wrapper div). With sections param returns JSON.
   * @param {string} responseText
   * @returns {string}
   */
  #extractSectionHtml(responseText) {
    const trimmed = responseText.trim();
    if (trimmed.startsWith('{')) {
      try {
        const data = JSON.parse(responseText);
        const sections = data.sections || data;
        const sectionId = (this.dataset.sectionId || this.sectionId || '').replace(/^shopify-section-/, '');
        const html = (typeof sections === 'object' && sections[sectionId]) || (sections && Object.values(sections)[0]);
        return typeof html === 'string' ? html : responseText;
      } catch {
        return responseText;
      }
    }
    return responseText;
  }

  #updateCanonicalCount() {
    if (!this.#canonicalRepresentativeIds || this.#canonicalRepresentativeIds.size === 0) return;
    const count = String(this.#canonicalRepresentativeIds.size);
    this.querySelectorAll('[data-testid="products-count"] span').forEach((span) => {
      const updated = span.textContent.replace(/\d+/, count);
      if (updated !== span.textContent) span.textContent = updated;
    });
  }

  /** @returns {Element | null} */
  #getGrid() {
    return this.refs?.grid || this.querySelector('[ref="grid"]') || this.querySelector('.product-grid');
  }

  #hideNonRepresentativeCards() {
    if (!this.#canonicalRepresentativeIds || this.#canonicalRepresentativeIds.size === 0) return;

    const grid = this.#getGrid();
    if (!grid) return;

    const cards = grid.querySelectorAll('[data-product-id]');
    cards.forEach((card) => {
      const id = card.getAttribute('data-product-id');
      if (id && !this.#canonicalRepresentativeIds.has(id)) {
        card.style.display = 'none';
      } else if (id) {
        card.style.display = '';
      }
    });
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
